import { Suspense, lazy, useEffect } from "react";
import { DEFAULT_LOCALE } from "@artlove365/shared";
import { Layout } from "@/components/Layout";
import { I18nProvider, useI18n, useUrlLocale } from "@/lib/i18n";
import { useRouter } from "@/lib/router";
import { Home } from "@/pages/Home";
import { AccountProvider } from "@/lib/account";
import { ORDERING_ENABLED, TAXON_KIND, type TaxonKindPath } from "@artlove365/shared";

// Every page except Home is its own chunk: visitors download only what the page needs.
const Gallery = lazy(() => import("@/pages/Gallery").then((m) => ({ default: m.Gallery })));
const Artists = lazy(() => import("@/pages/Artists").then((m) => ({ default: m.Artists })));
const ArtworkPage = lazy(() => import("@/pages/ArtworkPage").then((m) => ({ default: m.ArtworkPage })));
const Custom = lazy(() => import("@/pages/Custom").then((m) => ({ default: m.Custom })));
const Track = lazy(() => import("@/pages/Track").then((m) => ({ default: m.Track })));
const Admin = lazy(() => import("@/pages/Admin").then((m) => ({ default: m.Admin })));
const Checkout = lazy(() => import("@/pages/Checkout").then((m) => ({ default: m.Checkout })));
const Favorites = lazy(() => import("@/pages/Favorites").then((m) => ({ default: m.Favorites })));
const ArtistPage = lazy(() => import("@/pages/ArtistPage").then((m) => ({ default: m.ArtistPage })));
const HowItWorks = lazy(() => import("@/pages/HowItWorks").then((m) => ({ default: m.HowItWorks })));
const TaxaIndex = lazy(() => import("@/pages/Taxa").then((m) => ({ default: m.TaxaIndex })));
const TaxonPage = lazy(() => import("@/pages/Taxa").then((m) => ({ default: m.TaxonPage })));

export function App() {
  const locale = useUrlLocale();
  const { navigate, loc } = useRouter();
  const isAdmin = loc.pathname === "/admin" || loc.pathname.startsWith("/admin/");
  // In production the edge worker redirects "/"; this covers `vite dev` and unknown prefixes.
  useEffect(() => { if (!locale && !isAdmin) navigate(`/${DEFAULT_LOCALE}`); }, [locale, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps
  if (isAdmin) return <Suspense fallback={null}><Admin /></Suspense>;
  if (!locale) return null;
  return (
    <I18nProvider locale={locale}>
      <AccountProvider>
        <Layout><Suspense fallback={<div className="min-h-[60vh]" />}><Routes /></Suspense></Layout>
      </AccountProvider>
    </I18nProvider>
  );
}

function Routes() {
  const { loc } = useRouter();
  const { t } = useI18n();
  const [, , section, param] = loc.pathname.replace(/\/+$/, "").split("/");
  switch (section) {
    case undefined: case "": return <Home />;
    case "gallery": return <Gallery />;
    case "artists": return param ? <ArtistPage slug={param} /> : <Artists />;
    case "artworks": return param ? <ArtworkPage slug={param} /> : <Gallery />;
    case "custom": return <Custom />;
    case "favorites": return <Favorites />;
    case "how-it-works": return <HowItWorks />;
    // Ordering is hidden for now: these routes only exist when ORDERING_ENABLED is true.
    case "orders": if (ORDERING_ENABLED) return <Track code={param} />; break;
    case "checkout": if (ORDERING_ENABLED) return param ? <Checkout code={param} /> : <Track />; break;
  }
  if (section && section in TAXON_KIND) {
    const kind = section as TaxonKindPath;
    return param ? <TaxonPage kind={kind} slug={param} /> : <TaxaIndex kind={kind} />;
  }
  return <p className="mx-auto max-w-6xl px-4 py-20 font-serif text-3xl">{t("notFound.title")}</p>;
}
